import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { ServiceAddonMapping } from '../../types/service';
import { logger } from '../../utils/logger';
import { serviceAddonMappingEndpoints } from '../../services/api/endpoints/serviceAddonMapping.endpoints';

export const useServiceAddonMapping = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const createAddonMapping = async (
    serviceId: number,
    serviceAddonId: number
  ): Promise<ServiceAddonMapping | null> => {
    if (!selectedOrganizationId) {
      setError('No organization selected');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      return await serviceAddonMappingEndpoints.createAddonMapping(
        selectedOrganizationId,
        serviceId,
        serviceAddonId
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create service addon mapping';
      setError(message);
      logger.error('Error creating service addon mapping:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAddonMapping = async (mappingId: number): Promise<boolean> => {
    if (!selectedOrganizationId) {
      setError('No organization selected');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      await serviceAddonMappingEndpoints.deleteAddonMapping(selectedOrganizationId, mappingId);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete service addon mapping';
      setError(message);
      logger.error('Error deleting service addon mapping:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createAddonMapping,
    deleteAddonMapping,
    isLoading,
    error,
  };
};
